import React from 'react';
import { Card, Button, Typography, Box, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { NotaryService } from '../../../sponsfi/services/NotaryService';

interface NotaryRequest {
  id: string;
  documentName: string;
  type: string;
  status: string;
  requestDate: string;
}

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: '#1a1f2e',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  margin: '20px',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #2563eb, #059669)',
  padding: '28px 32px',
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const TableContainer = styled(Box)(({ theme }) => ({
  padding: '24px 32px',
  '& table': {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 8px',
    '& th': {
      color: '#8c96a5',
      fontWeight: 500,
      textAlign: 'left',
      padding: '12px 20px',
      borderBottom: '1px solid #2d3748',
      fontSize: '0.95rem',
    },
    '& td': {
      padding: '16px 20px',
      color: '#e2e8f0',
      background: '#22273a',
      fontSize: '0.95rem',
      '&:first-of-type': {
        borderTopLeftRadius: '8px',
        borderBottomLeftRadius: '8px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '8px',
        borderBottomRightRadius: '8px',
      },
    },
    '& tr:hover td': {
      background: '#2a2f44',
    },
  },
}));

const StatusChip = styled(Chip)(({ status }: { status: string }) => ({
  borderRadius: '16px',
  fontWeight: 500,
  padding: '0 12px',
  height: '28px',
  ...(status === 'Pending' && {
    backgroundColor: 'rgba(255, 215, 0, 0.15)',
    color: '#ffd700',
    border: '1px solid rgba(255, 215, 0, 0.3)',
  }),
  ...(status === 'Completed' && {
    backgroundColor: 'rgba(74, 222, 128, 0.15)',
    color: '#4ade80',
    border: '1px solid rgba(74, 222, 128, 0.3)',
  }),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  color: 'white',
  padding: '10px 24px',
  borderRadius: '8px',
  textTransform: 'none',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    transform: 'translateY(-1px)',
  },
}));

const ViewButton = styled(Button)(({ theme }) => ({
  color: '#60a5fa',
  padding: '6px 16px',
  borderRadius: '6px',
  textTransform: 'none',
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(96, 165, 250, 0.08)',
  },
}));

export const NotaryServiceComponent: React.FC = () => {
  console.log("NotaryServiceComponent rendering");
  
  const [notaryRequests, setNotaryRequests] = React.useState<NotaryRequest[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    console.log("NotaryServiceComponent useEffect running");
    
    // Mock data
    const mockData: NotaryRequest[] = [
      {
        id: '1',
        documentName: 'Form 990',
        type: 'Tax Document',
        status: 'Pending',
        requestDate: new Date().toLocaleDateString()
      }
    ];

    setNotaryRequests(mockData);
    setLoading(false);
  }, []);

  console.log("NotaryServiceComponent state:", { loading, error, requestsCount: notaryRequests.length });

  if (loading) return <div>Loading notary requests...</div>;
  if (error) return <div>Error loading notary requests: {error}</div>;

  return (
    <StyledCard>
      <HeaderSection>
        <Box>
          <Typography variant="h4" sx={{ 
            color: 'white', 
            fontWeight: 600,
            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
            letterSpacing: '-0.02em',
          }}>
            Document Notarization
          </Typography>
          <Typography variant="subtitle1" sx={{ 
            color: 'rgba(255,255,255,0.9)', 
            mt: 1,
            fontWeight: 400,
            letterSpacing: '0.01em',
          }}>
            Manage and track your document notarization requests
          </Typography>
        </Box>
        <ActionButton
          startIcon={<i className="fas fa-plus" style={{ fontSize: '0.875rem' }} />}
          variant="contained"
        >
          New Notary Request
        </ActionButton>
      </HeaderSection>

      <TableContainer>
        <table>
          <thead>
            <tr>
              <th>Document</th>
              <th>Type</th>
              <th>Status</th>
              <th>Requested</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {notaryRequests.map((request) => (
              <tr key={request.id}>
                <td>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <i className="fas fa-file-alt" style={{ color: '#60a5fa' }} />
                    <Typography sx={{ fontWeight: 500 }}>{request.documentName}</Typography>
                  </Box>
                </td>
                <td>{request.type}</td>
                <td>
                  <StatusChip
                    status={request.status}
                    label={request.status}
                    size="small"
                  />
                </td>
                <td>{request.requestDate}</td>
                <td>
                  <ViewButton
                    startIcon={<i className="fas fa-eye" />}
                  >
                    VIEW
                  </ViewButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableContainer>
    </StyledCard>
  );
}; 